import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { isEmpty, isNil, keyBy } from 'lodash-es';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { assetsApi, AssetShape } from '@blockworks/platform/api/research/assets';
import { slugify } from '@blockworks/platform/utils';
import { Link } from '@blockworks/ui/legacy/link';
import { RenderHtml, RenderSimpleHtml } from '@blockworks/ui/legacy/string';
import { cn } from '@blockworks/ui/utils';

import AdContainerIds from '@/components/ads/ad-container-ids';
import useAdSlot from '@/components/ads/hooks/use-ad-slot';
import PriceTicker from '@/components/common/price-ticker';
import Breadcrumb from '@/components/display/headers/breadcrumb';
import ByLine from '@/components/pages/frontpage/components/feature/middle/by-line';
import Newsletter from '@/components/pages/frontpage/components/feature/right/newsletter';
import { Author, TagsInfo } from '@/types/article-type';
import { NextImage } from '@/types/next-image';
import copyToClipboard from '@/utils/functions/browser/copy-to-clipboard';
import decodeEntities from '@/utils/functions/string/decode-entities';
import subscribeToNewsletter from '@/utils/functions/subscribe-to-newsletter';
import useAnalyticsReadTracker from '@/utils/hooks/use-analytics-read-tracker';
import useBlurhashPlaceholder from '@/utils/hooks/use-blurhash-placeholder';

import { generateTokenRequestNames, inlineTokenTicker } from '../rehype-plugins/inline-token-ticker';
import internalLinkingFix from '../rehype-plugins/internal-linking-fix';
import keyTakeAwaysHeader from '../rehype-plugins/key-take-away-header';
import rehypeAdPlacement from '../rehype-plugins/rehype-ad-placement';
import subscribeForm from '../rehype-plugins/subscribe-form';
import { MappedTokenType } from '../utils/get-news-article-data';
import { DEFAULT_SUBSCRIPTION_DATA, getBeehiivPublicationsData } from '../utils/get-newsletter-subscription-data';

type ArticleContentProps = {
    slug: string;
    title: string;
    blurhash?: string | null;
    authors: Author[];
    publishedAt: string;
    lastUpdate: string;
    content: string;
    shareUrl: string;
    className?: string;
    excerpt?: string | null;
    categories?: string[];
    tags?: TagsInfo[];
    imageUrl?: string | null;
    thumbnail?: NextImage;
    hideKeyTakeaways?: boolean;
};

const OPINION_CATEGORY = 'Opinion';

const ArticleContent = (props: ArticleContentProps) => {
    const router = useRouter();
    const {
        className,
        content,
        categories,
        tags,
        excerpt,
        imageUrl,
        blurhash,
        thumbnail,
        title,
        slug,
        publishedAt,
        lastUpdate,
        authors,
        shareUrl,
        // hideKeyTakeaways,
    } = props;

    const [tokenData, setTokenData] = useState<MappedTokenType>({});
    const [searchTokenNames, setSearchTokenNames] = useState<string[]>([]);

    useEffect(() => {
        const fetchFreshTokenData = async () => {
            try {
                const cachedAssets = (
                    await assetsApi.get.assets({
                        assets: searchTokenNames,
                        order: ['marketcap', 'asc'],
                    })
                )?.data;

                if (cachedAssets) {
                    setTokenData(keyBy(cachedAssets, item => item.code));
                }
            } catch (err) {
                console.error('Failed to fetch asset data:', err);
            }
        };

        if (searchTokenNames.length > 0) {
            fetchFreshTokenData();
        }
    }, [searchTokenNames]);

    const adAttributes = useAdSlot(AdContainerIds.NewsArticleMidArticle300x250);

    const isLiveUpdate = useMemo(() => tags?.some(tag => tag.name === 'Live Updates'), [tags]);

    /// Analytics start
    const articleTrackingData = React.useMemo(
        () => ({
            title,
            slug,
            tags,
            categories,
            authors: authors.map(a => a.title),
        }),
        [tags, categories, title, authors, slug],
    );

    const tagsElementRef = useAnalyticsReadTracker('Article.Read', articleTrackingData, slug);
    /// Analytics end

    const placeholderConfig = useBlurhashPlaceholder(blurhash);

    const subscriptionData =
        !isNil(categories) && !isEmpty(categories) ? getBeehiivPublicationsData(categories) : DEFAULT_SUBSCRIPTION_DATA;

    const commonRehypePlugins = [
        keyTakeAwaysHeader,
        () => rehypeAdPlacement({ adAttributes }),
        internalLinkingFix,
        subscribeForm,
        () => inlineTokenTicker(tokenData),
    ];
    const commonComponents = {
        'subscribe-form': () => (
            <div className="not-prose mb-6">
                <Newsletter
                    title="Newsletter"
                    subTitle={`Subscribe to ${subscriptionData.name}`}
                    inputPlaceholder="Email address"
                    buttonText="Subscribe"
                    className="pt-8 pb-8 px-0 border-t-gray-200 border-b border-t border-b-gray-200"
                    onButtonClick={({ email, captchaToken }) =>
                        subscribeToNewsletter({
                            email,
                            subscribeTo: [subscriptionData.subscriptionType],
                            source: 'inline-article-subscription-form',
                            sourcePath: router.asPath,
                            captchaToken,
                        })
                    }
                />
            </div>
        ),
        'inline-ticker': (props: {
            node?: {
                data?: AssetShape;
            };
        }) => {
            if (
                !props.node ||
                !props.node.data ||
                !props.node.data.code ||
                !props.node.data.change ||
                !props.node.data.change.usd24h ||
                !props.node.data.imageUrl
            ) {
                return <></>;
            }

            return (
                <span className="not-prose inline">
                    <PriceTicker
                        title={props.node.data.code}
                        priceChange={Number(props.node.data.change.usd24h.toFixed(2))}
                        src={props.node.data.imageUrl}
                    />
                </span>
            );
        },
    };

    return (
        <article className={cn('flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 gap-5', className)}>
            <div className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 relative gap-3 w-full">
                <div className="flex w-full">
                    <div className="flex">
                        <Breadcrumb
                            items={[
                                { title: 'news', link: '/news' },
                                ...(categories ?? []).map(category => ({
                                    title: category,
                                    link: `/category/${slugify(category || '')}`,
                                })),
                            ]}
                            className="flex-wrap"
                        />
                    </div>
                    <div className="grow"></div>
                    {isLiveUpdate && (
                        <div className="flex-none">
                            <div className="bg-red-600 px-2 py-0 flex">
                                <span className="relative flex h-2 w-2 mr-2 justify-center pt-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                                </span>
                                <span className="text-event-accent text-white text-sm uppercase">Live</span>
                            </div>
                        </div>
                    )}
                </div>
                <h1 className="self-stretch flex-grow-0 flex-shrink-0 text-xl md:text-3xl lg:text-4xl xl:text-5xl font-headline text-left text-dark">
                    {decodeEntities(title)}
                </h1>
                <p className="flex-grow-0 flex-shrink-0 text-md lg:text-lg text-left text-dark">{excerpt}</p>
                <div className="flex flex-col md:flex-row gap-2 content-baseline align-middle justify-start">
                    {categories?.[0] === OPINION_CATEGORY && <span className="text-red-solid text-xs">OPINION</span>}
                    <ByLine
                        className="text-xs"
                        authors={authors}
                        publishedAt={isLiveUpdate ? lastUpdate : publishedAt}
                        showTime
                        prefetchAuthor
                    />
                </div>
            </div>
            {imageUrl && (
                <div className="w-full relative">
                    <Image
                        {...placeholderConfig}
                        src={imageUrl}
                        width={980}
                        height={551.25}
                        sizes="100%"
                        className="object-cover w-full"
                        alt="article-image"
                        priority
                    />
                    {thumbnail?.caption && (
                        <RenderSimpleHtml
                            html={thumbnail?.caption}
                            className="absolute w-full text-right mt-1 mb-0 pb-0 leading-1 text-xxs text-gray-500"
                        />
                    )}
                </div>
            )}
            <div className="flex flex-col flex-wrap xl:flex-row xl:flex-nowrap gap-6 mt-2 md:mt-4 xl:mt-6 w-full">
                <div className="relative xl:sticky self-start lg:top-8 pb-0 md:pb-4 lg:pb-8 basis-4/4 lg:basis-1/4 w-full">
                    {/*
                    {tocCount > 0 && (
                        <div className="gap-5 p-4 md:p-5 lg:p-10 bg-[color:#f7f7f7] w-full">
                            <div className="pb-8 flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-3">
                                <p className="flex-grow-0 flex-shrink-0 text-sm text-left uppercase text-[#5637cd]">
                                    index
                                </p>
                                <svg
                                    width="161"
                                    height="2"
                                    viewBox="0 0 161 2"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow"
                                    preserveAspectRatio="none"
                                >
                                    <line y1="0.75" x2="161" y2="0.75" stroke="#B4B4B4" strokeOpacity="0.35" />
                                </svg>
                            </div>
                            <div>
                                {!hideKeyTakeaways && (
                                    <ol className="toc-level toc-level-1">
                                        <li className="toc-item toc-item-h3">
                                            <a className="page-link page-link-h3" href="#key-takeaways">
                                                Key Takeaways
                                            </a>
                                        </li>
                                    </ol>
                                )}
                                {toc}
                            </div>
                        </div>
                    )}
                    */}
                    <div className="relative flex xl:flex-col gap-4 justify-center align-middle pt-6 md:px-10 md:py-6 md:bg-gray-50 text-gray-600">
                        <span className="mt-0.5 text-sm text-left uppercase text-brand">share</span>
                        <div className="flex flex-row gap-4">
                            <a
                                className="cursor-pointer hover:text-brand"
                                href={`https://twitter.com/intent/tweet?text=${title}&url=${shareUrl}&via=Blockworks_`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    width="24"
                                    height="25"
                                    viewBox="0 0 24 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow-0 flex-shrink-0 w-6 h-6 relative"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M20 8.3374C19.4 8.6374 18.8 8.7374 18.1 8.8374C18.8 8.4374 19.3 7.8374 19.5 7.0374C18.9 7.4374 18.2 7.6374 17.4 7.8374C16.8 7.2374 15.9 6.8374 15 6.8374C13.3 6.8374 11.8 8.3374 11.8 10.1374C11.8 10.4374 11.8 10.6374 11.9 10.8374C9.2 10.7374 6.7 9.4374 5.1 7.4374C4.8 7.9374 4.7 8.4374 4.7 9.1374C4.7 10.2374 5.3 11.2374 6.2 11.8374C5.7 11.8374 5.2 11.6374 4.7 11.4374C4.7 13.0374 5.8 14.3374 7.3 14.6374C7 14.7374 6.7 14.7374 6.4 14.7374C6.2 14.7374 6 14.7374 5.8 14.6374C6.2 15.9374 7.4 16.9374 8.9 16.9374C7.8 17.8374 6.4 18.3374 4.8 18.3374C4.5 18.3374 4.3 18.3374 4 18.3374C5.5 19.2374 7.2 19.8374 9 19.8374C15 19.8374 18.3 14.8374 18.3 10.5374C18.3 10.4374 18.3 10.2374 18.3 10.1374C19 9.6374 19.6 9.0374 20 8.3374Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </a>
                            <a
                                className="cursor-pointer hover:text-brand"
                                href={`http://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    width="24"
                                    height="25"
                                    viewBox="0 0 24 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow-0 flex-shrink-0 w-6 h-6 relative"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M19.3 4.8374H4.7C4.3 4.8374 4 5.1374 4 5.5374V20.2374C4 20.5374 4.3 20.8374 4.7 20.8374H19.4C19.8 20.8374 20.1 20.5374 20.1 20.1374V5.5374C20 5.1374 19.7 4.8374 19.3 4.8374ZM8.7 18.4374H6.4V10.8374H8.8V18.4374H8.7ZM7.6 9.8374C6.8 9.8374 6.2 9.1374 6.2 8.4374C6.2 7.6374 6.8 7.0374 7.6 7.0374C8.4 7.0374 9 7.6374 9 8.4374C8.9 9.1374 8.3 9.8374 7.6 9.8374ZM17.6 18.4374H15.2V14.7374C15.2 13.8374 15.2 12.7374 14 12.7374C12.8 12.7374 12.6 13.7374 12.6 14.7374V18.5374H10.2V10.8374H12.5V11.8374C12.8 11.2374 13.6 10.6374 14.7 10.6374C17.1 10.6374 17.5 12.2374 17.5 14.2374V18.4374H17.6Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </a>
                            <a
                                className="cursor-pointer hover:text-brand"
                                href={`https://t.me/share/url?url=${shareUrl}&text=Blockworks:%20${title}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    className="mt-1"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 17 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M15.3158 0.996943C15.109 1.00765 14.9143 1.07457 14.7411 1.14952H14.7387C14.5632 1.22715 13.7289 1.61797 12.4618 2.20955C11.1947 2.80381 9.55008 3.57474 7.9175 4.34032C4.65716 5.8688 1.45212 7.37319 1.45212 7.37319L1.49059 7.35713C1.49059 7.35713 1.26938 7.43743 1.03856 7.6141C0.920749 7.69976 0.790912 7.81754 0.677906 8.00224C0.5649 8.18695 0.473533 8.47069 0.507195 8.76247C0.562496 9.25768 0.851022 9.55481 1.0578 9.7181C1.26698 9.88407 1.46654 9.96169 1.46654 9.96169H1.47135L4.47683 11.0886C4.61148 11.5705 5.3929 14.4293 5.58044 15.0879C5.69105 15.4813 5.79924 15.7276 5.93389 15.915C5.99881 16.0114 6.07575 16.0917 6.16711 16.1559C6.20318 16.18 6.24165 16.1987 6.28012 16.2148C6.29214 16.2228 6.30416 16.2255 6.31859 16.2282L6.28733 16.2202C6.29695 16.2228 6.30416 16.2309 6.31138 16.2335C6.33542 16.2416 6.35225 16.2442 6.38351 16.2496C6.85957 16.4102 7.24187 16.081 7.24187 16.081L7.26351 16.0622L9.03795 14.2634L12.0122 16.8037L12.0795 16.8358C12.6998 17.1383 13.3274 16.9697 13.6592 16.6725C13.9934 16.3727 14.1232 15.9899 14.1232 15.9899L14.1449 15.9284L16.4434 2.81987C16.5084 2.49598 16.5252 2.19349 16.4531 1.89904C16.3809 1.60459 16.1958 1.32887 15.9722 1.18165C15.7462 1.03174 15.5226 0.986236 15.3158 0.996943ZM15.2533 2.40229C15.2509 2.44512 15.2581 2.43976 15.2413 2.52274V2.53077L12.9643 15.5028C12.9547 15.5215 12.9379 15.5617 12.8922 15.6018C12.8441 15.6446 12.8056 15.6714 12.6061 15.5831L8.96822 12.4779L6.77061 14.7077L7.23225 11.4259C7.23225 11.4259 12.9306 5.51278 13.1759 5.25848C13.4211 5.00418 13.3394 4.95064 13.3394 4.95064C13.3562 4.64013 12.9691 4.85963 12.9691 4.85963L5.47465 10.0286L5.47225 10.0152L1.8801 8.66878V8.6661C1.87769 8.6661 1.87288 8.66343 1.87048 8.66343C1.87288 8.66343 1.88972 8.65539 1.88972 8.65539L1.90895 8.64469L1.92819 8.63666C1.92819 8.63666 5.13563 7.13227 8.39598 5.60379C10.0286 4.83821 11.6732 4.06728 12.9379 3.47302C14.2026 2.88144 15.1379 2.44779 15.1908 2.4237C15.2413 2.40229 15.2172 2.40229 15.2533 2.40229Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </a>
                            <a
                                className="cursor-pointer hover:text-brand"
                                href={`mailto:?subject=${title}&body=${shareUrl}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 25 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow-0 flex-shrink-0 w-6 h-6 relative"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        d="M19.15 6.25H6.35C5.47 6.25 4.758 6.97 4.758 7.85L4.75 17.45C4.75 18.33 5.47 19.05 6.35 19.05H19.15C20.03 19.05 20.75 18.33 20.75 17.45V7.85C20.75 6.97 20.03 6.25 19.15 6.25ZM20.75 7.85L12.75 13.45L4.758 7.85C4.77224 7.35781 4.88776 7.11994 5.25 6.75L12.75 11.85L20.25 6.75L20.75 7.85Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </a>
                            <span className="cursor-pointer hover:text-brand" onClick={() => copyToClipboard(shareUrl)}>
                                <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 24 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-6.5 h-6.5 relative"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M3.9 12.25C3.9 10.54 5.29 9.15 7 9.15H11V7.25H7C4.24 7.25 2 9.49 2 12.25C2 15.01 4.24 17.25 7 17.25H11V15.35H7C5.29 15.35 3.9 13.96 3.9 12.25ZM8 13.25H16V11.25H8V13.25ZM17 7.25H13V9.15H17C18.71 9.15 20.1 10.54 20.1 12.25C20.1 13.96 18.71 15.35 17 15.35H13V17.25H17C19.76 17.25 22 15.01 22 12.25C22 9.49 19.76 7.25 17 7.25Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </span>
                        </div>
                    </div>
                    {/* <AdContainer
                        googleAd={AdContainerIds.NewsArticleLeft300x300!}
                        className="invisible lg:visible w-full h-0 w-0 xl:h-[300px] xl:w-[300px] xl:mt-8 text-center align-middle"
                    /> */}
                </div>
                <div className="p-2 basis-4/4 xl:basis-3/4">
                    <section className="w-full">
                        {isEmpty(tokenData) && (
                            <RenderHtml
                                rehypePlugins={[
                                    () => generateTokenRequestNames(setSearchTokenNames),
                                    ...commonRehypePlugins,
                                ]}
                                className="prose prose-purple max-w-none prose-p:text-justify prose-p:mt-0 prose-p:mb-6 prose-h2:text-xl prose-headings:scroll-mt-28 prose-headings:font-normal lg:prose-headings:scroll-mt-[8.5rem] prose-lead:text-purple-500 prose-a:font-base prose-a:border-none prose-a:hover:border-none prose-a:underline prose-pre:rounded-xl prose-pre:bg-purple-900 prose-pre:shadow-lg"
                                html={content}
                                components={commonComponents}
                            />
                        )}

                        {!isEmpty(tokenData) && (
                            <RenderHtml
                                rehypePlugins={[...commonRehypePlugins]}
                                className="prose prose-purple max-w-none prose-p:text-justify prose-p:mt-0 prose-p:mb-6 prose-h2:text-xl prose-headings:scroll-mt-28 prose-headings:font-normal lg:prose-headings:scroll-mt-[8.5rem] prose-lead:text-purple-500 prose-a:font-base prose-a:border-none prose-a:hover:border-none prose-a:underline prose-pre:rounded-xl prose-pre:bg-purple-900 prose-pre:shadow-lg"
                                html={content}
                                components={commonComponents}
                            />
                        )}
                    </section>
                    {tags && (
                        <section ref={tagsElementRef} className="flex gap-6 mt-4 py-8 uppercase">
                            <span className="text-gray-800">Tags</span>
                            <ul className="flex flex-row flex-wrap gap-3 divide ">
                                {tags.map(tag => (
                                    <li key={tag.name}>
                                        <Link href={`/tag/${tag.slug}`} className="text-brand hover:text-gray-800">
                                            {decodeEntities(tag.name)}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>
            </div>
        </article>
    );
};

export default ArticleContent;
